import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { KeyValue } from '@/components/inspect/KeyValue';
import { Banner, EmptyState, ErrorPage, Spinner } from '@/components/primitives';
import { useNatsMessages } from '@/hooks/useSturmData';
import { bytes, count, timestamp } from '@/lib/inspectFormat';
import type { NatsMessage } from '@/types/inspect';

const LIMITS = [10, 25, 50, 100];

export function MessagePeek({ stream }: { stream: string }) {
  const [subject, setSubject] = useState('');
  const [appliedSubject, setAppliedSubject] = useState('');
  const [limit, setLimit] = useState(25);
  const [startSeq, setStartSeq] = useState<number | undefined>(undefined);
  const [enabled, setEnabled] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const query = { subject: appliedSubject || undefined, startSeq, limit };
  const { data, isFetching, isError, error } = useNatsMessages(stream, query, enabled);

  const load = (seq?: number) => {
    setAppliedSubject(subject.trim());
    setStartSeq(seq);
    setEnabled(true);
  };

  return (
    <div>
      <Banner>
        <strong>Read-only.</strong> Messages are fetched with <span className="mono">STREAM.MSG.GET</span>
        {' '}— nothing is consumed or acknowledged, no cursor moves, and no consumer is created.
      </Banner>

      <div className="filterbar mb-16">
        <input
          className="input mono"
          placeholder="datahub.contacts.*"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <select
          className="select"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {LIMITS.map((value) => (
            <option key={value} value={value}>
              {value} messages
            </option>
          ))}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={() => load(undefined)} disabled={isFetching}>
          {isFetching ? <Spinner size={12} /> : <Icon name="eye" size={12} />} Newest
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => load(data?.nextSeq ?? undefined)}
          disabled={isFetching || !data?.nextSeq}
        >
          <Icon name="arrowRight" size={12} /> Older
        </button>
      </div>

      {isError ? (
        <ErrorPage
          title="Could not read messages"
          message={error instanceof Error ? error.message : 'The broker did not respond.'}
        />
      ) : null}

      {!enabled ? (
        <EmptyState
          title="Nothing fetched yet"
          sub="Choose a subject filter and a size, then load the newest messages."
        />
      ) : null}

      {enabled && !isError && data?.messages.length === 0 ? (
        <EmptyState
          title="No messages matched"
          sub={
            appliedSubject
              ? `Nothing on ${appliedSubject} within the probe budget.`
              : 'The stream holds no messages in this range.'
          }
        />
      ) : null}

      {data?.messages.map((message) => (
        <MessageRow
          key={message.sequence}
          message={message}
          open={expanded === message.sequence}
          onToggle={() => setExpanded(expanded === message.sequence ? null : message.sequence)}
        />
      ))}

      {data && data.messages.length > 0 ? (
        <div className="tiny muted">
          {count(data.messages.length)} message(s) · {count(data.probesUsed)} sequence lookups
          {data.truncated ? ' · stopped early at the response budget' : ''}
        </div>
      ) : null}
    </div>
  );
}

function MessageRow({
  message,
  open,
  onToggle,
}: {
  message: NatsMessage;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="card mb-8">
      <div className="card-pad row gap-8" onClick={onToggle} style={{ cursor: 'pointer' }}>
        <Icon name={open ? 'chevronDown' : 'chevron'} size={14} />
        <span className="mono tbl-num">#{message.sequence}</span>
        <span className="mono flex1">{message.subject}</span>
        <span className="badge">{message.encoding}</span>
        <span className="tiny muted">{bytes(message.size)}</span>
        <span className="tiny muted">{timestamp(message.timestamp)}</span>
      </div>

      {open ? (
        <div className="card-pad" style={{ paddingTop: 0 }}>
          {message.headers.length > 0 ? (
            <div className="kv mb-8">
              {message.headers.map((header) => (
                <KeyValue key={header.name} k={header.name} v={header.value} mono />
              ))}
            </div>
          ) : null}

          <div className="json">{message.preview || '(empty body)'}</div>

          {message.truncated ? (
            <div className="tiny muted" style={{ marginTop: 6 }}>
              Showing the first {bytes(message.preview.length)} of {bytes(message.size)}.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
